import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { chamarApi, ErroApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import {
  consultaDaClassificacao,
  type DadosDeVerificacao,
  type FiltroDaClassificacao,
  type LinhaPublica,
  type Pagina,
  type SorteioPublico,
  type SorteioPublicoResumo,
  type VerificacaoDoSorteio,
} from './contrato';

/**
 * Camada de dados pública do sorteio. Dado real, da API
 * `bff/v1/sorteios-publicos` (frente F08), sem sessão (`publica: true`): a
 * leitura é anônima e não traz placa nem e-mail. Nenhum mock.
 */
export const CAMINHO_PUBLICO = '/bff/v1/sorteios-publicos';

const CHAVES = {
  lista: (pagina: number, tamanho: number) =>
    ['sorteios-publicos', 'lista', pagina, tamanho] as const,
  detalhe: (id: string) => ['sorteios-publicos', 'detalhe', id] as const,
  classificacao: (id: string, consulta: string) =>
    ['sorteios-publicos', 'classificacao', id, consulta] as const,
  dados: (id: string) => ['sorteios-publicos', 'dados-verificacao', id] as const,
  verificacao: (id: string) => ['sorteios-publicos', 'verificacao', id] as const,
};

const semInsistirEm404 = (tentativas: number, erro: unknown) =>
  !(erro instanceof ErroApi && erro.situacao === 404) && naoInsistirEmRecusa(tentativas, erro);

const doSorteio = (id: string) => `${CAMINHO_PUBLICO}/${encodeURIComponent(id)}`;

export function useSorteiosPublicos(pagina: number, tamanho: number) {
  return useQuery({
    queryKey: CHAVES.lista(pagina, tamanho),
    queryFn: () =>
      chamarApi<Pagina<SorteioPublicoResumo>>(
        `${CAMINHO_PUBLICO}?pagina=${pagina}&tamanho=${tamanho}`,
        { publica: true },
      ),
    retry: naoInsistirEmRecusa,
    placeholderData: keepPreviousData,
  });
}

export function useSorteioPublico(id: string) {
  return useQuery({
    queryKey: CHAVES.detalhe(id),
    queryFn: () => chamarApi<SorteioPublico>(doSorteio(id), { publica: true }),
    retry: semInsistirEm404,
  });
}

export function useClassificacaoPublica(
  id: string,
  filtro: FiltroDaClassificacao,
  pagina: number,
  tamanho: number,
  ativa: boolean,
) {
  const consulta = consultaDaClassificacao(filtro, pagina, tamanho);
  return useQuery({
    queryKey: CHAVES.classificacao(id, consulta),
    queryFn: () =>
      chamarApi<Pagina<LinhaPublica>>(`${doSorteio(id)}/classificacao?${consulta}`, {
        publica: true,
      }),
    retry: naoInsistirEmRecusa,
    placeholderData: keepPreviousData,
    enabled: ativa,
  });
}

/**
 * Os dados de verificação (universo inteiro, resultado, semente, trilha). Só
 * são buscados quando a pessoa pede — são o maior corpo desta API.
 */
export function useDadosDeVerificacao(id: string) {
  return useQuery({
    queryKey: CHAVES.dados(id),
    queryFn: () =>
      chamarApi<DadosDeVerificacao>(`${doSorteio(id)}/dados-verificacao`, { publica: true }),
    retry: naoInsistirEmRecusa,
    enabled: false,
    staleTime: 0,
  });
}

/** A verificação feita pelo servidor (`sorteio_verificar`). */
export function useVerificacaoDoServidor(id: string) {
  return useQuery({
    queryKey: CHAVES.verificacao(id),
    queryFn: () =>
      chamarApi<VerificacaoDoSorteio>(`${doSorteio(id)}/verificacao`, { publica: true }),
    retry: naoInsistirEmRecusa,
    enabled: false,
    staleTime: 0,
  });
}
